import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ConfirmModal from "../../common/ConfirmModal";
import InfoAlert from "../../common/InfoAlert";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import {
  capacityManagementActivity,
  ensureCapacityLocationId,
  fetchCapacityBuildings,
  fetchCapacityFloors,
  fetchCapacityRooms,
  fetchOrganizationSites,
  firstSuccessRow,
  isCapacitySuccess,
  successRows,
  toLegacyBlockDateTime,
} from "../../../services/capacityManagementService";
import "./CapacityManagement.scss";

const idOf = (row, ...keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return "";
};

const nameOf = (row, ...keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return "";
};

const isBlocked = (row) => {
  const value = row?.IsBlocked ?? row?.isBlocked;
  return value === true || value === 1 || value === "1";
};

const capacityRoomRefId = (cap) =>
  idOf(cap, "RoomRefId", "RoomIdRef", "RoomId", "roomRefId");

const capacityTypeLabel = (cap) => {
  const raw = nameOf(cap, "CapacityType", "Type", "capacityType");
  if (/chair/i.test(raw)) return "Chair";
  if (/bed/i.test(raw)) return "Bed";
  return raw || "Bed";
};

const rowIsActive = (row) => {
  const value = row?.IsActive;
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  return (
    String(row?.Status || "")
      .trim()
      .toLowerCase() !== "inactive"
  );
};

const siteLabel = (row) => {
  const name = nameOf(row, "SiteName") || "Site";
  const abbr = nameOf(row, "SiteAbbr");
  return abbr ? `(${abbr}) ${name}` : name;
};

const EMPTY_FORM = { name: "", count: "1", type: "Bed" };
const EMPTY_BLOCK = {
  reason: "",
  startDate: "",
  startTime: "00:00",
  endDate: "",
  endTime: "23:59",
};

function RowActions({ blocked, onEdit, onDelete, onBlock, onUnblock, blockTitle }) {
  return (
    <div className="capacity-mgmt__actions" onClick={(e) => e.stopPropagation()}>
      {onEdit ? (
        <button type="button" title="Edit" onClick={onEdit}>
          <i className="fas fa-pencil-alt" aria-hidden="true" />
        </button>
      ) : null}
      {onDelete ? (
        <button type="button" title="Delete" onClick={onDelete}>
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      ) : null}
      {blocked ? (
        <button
          type="button"
          className="is-blocked"
          title={blockTitle || "Unblock"}
          onClick={onUnblock}
        >
          <i className="fas fa-ban" aria-hidden="true" />
        </button>
      ) : onBlock ? (
        <button type="button" className="is-open" title="Block" onClick={onBlock}>
          <i className="fas fa-ban" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

export default function CapacityManagement() {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [orgSites, setOrgSites] = useState([]);
  const [treeSearch, setTreeSearch] = useState("");
  /** siteId -> { locationId, buildings, byBuilding, loading } */
  const [siteCache, setSiteCache] = useState({});
  const siteCacheRef = React.useRef({});
  const [expandedSites, setExpandedSites] = useState({});
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [expandedFloors, setExpandedFloors] = useState({});
  /** { siteId, locationId, building, floor, room } */
  const [selectedRoom, setSelectedRoom] = useState(null);

  const [formModal, setFormModal] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null });
  const [blockModal, setBlockModal] = useState(null);
  const [blockValues, setBlockValues] = useState(EMPTY_BLOCK);

  const activeSites = useMemo(
    () => (orgSites || []).filter(rowIsActive),
    [orgSites],
  );

  const filteredSites = useMemo(() => {
    const q = treeSearch.trim().toLowerCase();
    if (!q) return activeSites;
    return activeSites.filter((s) => siteLabel(s).toLowerCase().includes(q));
  }, [activeSites, treeSearch]);

  const selectedCaps = useMemo(() => {
    if (!selectedRoom) return [];
    const roomId = idOf(selectedRoom.room, "RoomId");
    const pack =
      siteCache[selectedRoom.siteId]?.byBuilding?.[
        idOf(selectedRoom.building, "BuildingId")
      ]?.roomsByFloor?.[idOf(selectedRoom.floor, "FloorId")];
    return (pack?.capacities || []).filter(
      (c) => capacityRoomRefId(c).toLowerCase() === roomId.toLowerCase(),
    );
  }, [selectedRoom, siteCache]);

  const runAction = useCallback(
    async (overrides, { reload = null, successFallback = "Done." } = {}) => {
      if (!userData?.OrganizationId) return false;
      try {
        showLoader();
        const response = await capacityManagementActivity(userData, overrides);
        const rows = successRows(response);
        const row = rows[0] || firstSuccessRow(response);
        const ok =
          isCapacitySuccess(row) ||
          Number(response?.success) === 1 ||
          rows.some((item) => isCapacitySuccess(item));
        if (ok) {
          showAlert({
            title: "Success",
            message: row?.MSG || successFallback,
            type: "success",
          });
          if (typeof reload === "function") await reload(row, rows);
          return true;
        }
        showAlert({
          title: "Notice",
          message:
            row?.MSG ||
            response?.message ||
            "Request completed with no success flag.",
          type: "warning",
        });
        return false;
      } catch (err) {
        showAlert({
          title: "Error",
          message: err?.message || "Capacity management request failed.",
          type: "error",
        });
        return false;
      } finally {
        hideLoader();
      }
    },
    [hideLoader, showAlert, showLoader, userData],
  );

  const loadSiteTree = useCallback(
    async (site, { force = false } = {}) => {
      const siteId = idOf(site, "SiteId");
      if (!siteId || !userData?.OrganizationId) return null;

      const cached = siteCacheRef.current[siteId];
      if (!force && cached?.loaded) {
        return cached;
      }

      setSiteCache((current) => {
        const next = {
          ...current,
          [siteId]: {
            ...(current[siteId] || {}),
            loading: true,
          },
        };
        siteCacheRef.current = next;
        return next;
      });

      try {
        const ensured = await ensureCapacityLocationId(userData, site);
        const locationId = String(ensured.locationId || "").trim();
        const buildingResult = locationId
          ? await fetchCapacityBuildings(userData, locationId)
          : { buildings: [] };
        const buildings = buildingResult.buildings || [];
        const byBuilding = {};

        await Promise.all(
          buildings.map(async (building) => {
            const buildingId = idOf(building, "BuildingId");
            if (!buildingId || !locationId) return;
            const floors = await fetchCapacityFloors(
              userData,
              locationId,
              buildingId,
            );
            const roomsByFloor = {};
            await Promise.all(
              (floors || []).map(async (floor) => {
                const floorId = idOf(floor, "FloorId");
                if (!floorId) return;
                const pack = await fetchCapacityRooms(
                  userData,
                  locationId,
                  buildingId,
                  floorId,
                );
                roomsByFloor[floorId] = {
                  rooms: pack.rooms || [],
                  capacities: pack.capacities || [],
                };
              }),
            );
            byBuilding[buildingId] = { floors: floors || [], roomsByFloor };
          }),
        );

        const nextEntry = {
          locationId,
          buildings,
          byBuilding,
          loaded: true,
          loading: false,
        };
        setSiteCache((current) => {
          const next = { ...current, [siteId]: nextEntry };
          siteCacheRef.current = next;
          return next;
        });
        return nextEntry;
      } catch (err) {
        const failed = {
          locationId: "",
          buildings: [],
          byBuilding: {},
          loaded: false,
          loading: false,
        };
        setSiteCache((current) => {
          const next = { ...current, [siteId]: failed };
          siteCacheRef.current = next;
          return next;
        });
        showAlert({
          title: "Error",
          message: err?.message || "Could not load site capacity.",
          type: "error",
        });
        return null;
      }
    },
    [showAlert, userData],
  );

  const loadSites = useCallback(async () => {
    if (!userData?.OrganizationId) return;
    try {
      showLoader();
      const sites = await fetchOrganizationSites(userData);
      const list = Array.isArray(sites) ? sites : [];
      setOrgSites(list);
      const first = list.find(rowIsActive);
      if (first) {
        const siteId = idOf(first, "SiteId");
        setExpandedSites({ [siteId]: true });
        await loadSiteTree(first, { force: true });
      }
    } catch (err) {
      setOrgSites([]);
      showAlert({
        title: "Error",
        message: err?.message || "Could not load sites.",
        type: "error",
      });
    } finally {
      hideLoader();
    }
  }, [hideLoader, loadSiteTree, showAlert, showLoader, userData]);

  useEffect(() => {
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, userData?.StaffId]);

  const toggleSite = async (site) => {
    const siteId = idOf(site, "SiteId");
    const willOpen = !expandedSites[siteId];
    setExpandedSites((current) => ({ ...current, [siteId]: willOpen }));
    if (willOpen) {
      showLoader();
      try {
        await loadSiteTree(site);
      } finally {
        hideLoader();
      }
    }
  };

  const toggleBuilding = (buildingId) => {
    setExpandedBuildings((current) => ({
      ...current,
      [buildingId]: !current[buildingId],
    }));
  };

  const toggleFloor = (floorId) => {
    setExpandedFloors((current) => ({
      ...current,
      [floorId]: !current[floorId],
    }));
  };

  const selectRoom = (site, locationId, building, floor, room) => {
    setSelectedRoom({
      siteId: idOf(site, "SiteId"),
      site,
      locationId,
      building,
      floor,
      room,
    });
    setExpandedSites((c) => ({ ...c, [idOf(site, "SiteId")]: true }));
    setExpandedBuildings((c) => ({
      ...c,
      [idOf(building, "BuildingId")]: true,
    }));
    setExpandedFloors((c) => ({ ...c, [idOf(floor, "FloorId")]: true }));
  };

  const refreshSiteTree = useCallback(
    async (site, expand = {}) => {
      if (!site) return null;
      const siteId = idOf(site, "SiteId");
      const data = await loadSiteTree(site, { force: true });
      if (siteId) {
        setExpandedSites((current) => ({ ...current, [siteId]: true }));
      }
      const buildingId = String(expand.buildingId || "").trim();
      const floorId = String(expand.floorId || "").trim();
      if (buildingId) {
        setExpandedBuildings((current) => ({
          ...current,
          [buildingId]: true,
        }));
      }
      if (floorId) {
        setExpandedFloors((current) => ({ ...current, [floorId]: true }));
      }
      if (
        selectedRoom?.room &&
        data &&
        idOf(selectedRoom.site, "SiteId") === siteId
      ) {
        const selectedBuildingId = idOf(selectedRoom.building, "BuildingId");
        const selectedFloorId = idOf(selectedRoom.floor, "FloorId");
        const roomId = idOf(selectedRoom.room, "RoomId");
        const pack =
          data.byBuilding?.[selectedBuildingId]?.roomsByFloor?.[
            selectedFloorId
          ];
        const nextRoom = (pack?.rooms || []).find(
          (r) => idOf(r, "RoomId").toLowerCase() === roomId.toLowerCase(),
        );
        if (nextRoom) {
          setSelectedRoom((current) =>
            current
              ? {
                  ...current,
                  locationId: data.locationId,
                  room: nextRoom,
                  building:
                    (data.buildings || []).find(
                      (b) => idOf(b, "BuildingId") === selectedBuildingId,
                    ) || current.building,
                  floor:
                    (data.byBuilding?.[selectedBuildingId]?.floors || []).find(
                      (f) => idOf(f, "FloorId") === selectedFloorId,
                    ) || current.floor,
                }
              : current,
          );
        }
      }
      return data;
    },
    [loadSiteTree, selectedRoom],
  );

  const openForm = (kind, entity = null, context = {}) => {
    setFormModal({ kind, entity, context });
    if (kind === "capacity") {
      setFormValues({ name: "", count: "1", type: "Bed" });
    } else if (kind === "building" && !entity) {
      setFormValues({ name: "", count: "1", type: "Bed" });
    } else {
      setFormValues({
        name: nameOf(entity, "BuildingName", "FloorName", "RoomName") || "",
        count: String(entity?.BuildingTotalNumber ?? "1"),
        type: "Bed",
      });
    }
  };

  const closeForm = () => {
    setFormModal(null);
    setFormValues(EMPTY_FORM);
  };

  const resolveContextLocation = async (context) => {
    if (context?.locationId) return context.locationId;
    if (selectedRoom?.locationId) return selectedRoom.locationId;
    const site = context?.site;
    if (!site) return "";
    const data = await loadSiteTree(site);
    return data?.locationId || "";
  };

  const handleSaveForm = async () => {
    if (!formModal) return;
    const { kind, entity, context = {} } = formModal;
    const locationId = await resolveContextLocation(context);
    if (!locationId) {
      showAlert({
        title: "Validation",
        message: "Expand a site first.",
        type: "warning",
      });
      return;
    }
    const name = formValues.name.trim();
    const site = context.site || selectedRoom?.site;
    const reload = async (savedRow) => {
      await refreshSiteTree(site, {
        buildingId:
          idOf(context.building, "BuildingId") ||
          (kind === "building" ? idOf(savedRow, "BuildingId") : ""),
        floorId:
          idOf(context.floor, "FloorId") ||
          (kind === "floor" ? idOf(savedRow, "FloorId") : ""),
      });
    };

    if (kind === "building") {
      if (!name) {
        showAlert({
          title: "Validation",
          message: "Please enter a building name.",
          type: "warning",
        });
        return;
      }
      const ok = await runAction(
        {
          LocationId: locationId,
          BuildingId: idOf(entity, "BuildingId") || null,
          BuildingName: name,
          BuildingTotalNumber: entity ? null : Number(formValues.count) || 1,
          ActionType: entity ? "UpdateBuilding" : "AddBuilding",
        },
        {
          reload,
          successFallback: entity ? "Building updated." : "Building added.",
        },
      );
      if (ok) closeForm();
      return;
    }

    if (kind === "floor") {
      if (!name) {
        showAlert({
          title: "Validation",
          message: "Please enter a floor name.",
          type: "warning",
        });
        return;
      }
      const buildingId =
        idOf(context.building, "BuildingId") || idOf(entity, "BuildingId");
      const ok = await runAction(
        {
          LocationId: locationId,
          BuildingId: buildingId,
          FloorId: idOf(entity, "FloorId") || null,
          FloorName: name,
          ActionType: entity ? "UpdateFloor" : "AddFloor",
        },
        {
          reload,
          successFallback: entity ? "Floor updated." : "Floor added.",
        },
      );
      if (ok) closeForm();
      return;
    }

    if (kind === "room") {
      const buildingId = idOf(context.building, "BuildingId");
      const floorId = idOf(context.floor, "FloorId") || idOf(entity, "FloorId");
      if (entity) {
        if (!name) {
          showAlert({
            title: "Validation",
            message: "Please enter a room name.",
            type: "warning",
          });
          return;
        }
        const ok = await runAction(
          {
            LocationId: locationId,
            BuildingId: buildingId,
            FloorId: floorId,
            RoomId: idOf(entity, "RoomId"),
            RoomName: name,
            ActionType: "UpdateRoom",
          },
          { reload, successFallback: "Room updated." },
        );
        if (ok) closeForm();
        return;
      }
      const ok = await runAction(
        {
          LocationId: locationId,
          BuildingId: buildingId,
          FloorId: floorId,
          ActionType: "AddRoom",
        },
        { reload, successFallback: "Room added." },
      );
      if (ok) closeForm();
      return;
    }

    if (kind === "capacity") {
      const count = Number(formValues.count);
      if (!count || count < 1) {
        showAlert({
          title: "Validation",
          message: "Enter a valid capacity count.",
          type: "warning",
        });
        return;
      }
      const room = entity || context.room || selectedRoom?.room;
      const building = context.building || selectedRoom?.building;
      const floor = context.floor || selectedRoom?.floor;
      const ok = await runAction(
        {
          LocationId: locationId,
          BuildingId: idOf(building, "BuildingId"),
          FloorId: idOf(floor, "FloorId"),
          RoomId: idOf(room, "RoomId"),
          CapacityCount: count,
          CapacityType: formValues.type || "Bed",
          ActionType: "AddRoomCapacity",
        },
        { reload, successFallback: "Capacity added." },
      );
      if (ok) closeForm();
    }
  };

  const requestDelete = (action) => setConfirmModal({ open: true, action });

  const handleConfirmDelete = async () => {
    const { action } = confirmModal;
    setConfirmModal({ open: false, action: null });
    if (!action) return;
    const authRes = await openAuthModal({ showDescription: false });
    if (!authRes || authRes.Success !== 1) return;

    const locationId =
      action.locationId ||
      selectedRoom?.locationId ||
      (await resolveContextLocation(action.context || {}));
    if (!locationId) return;

    const { type, row, context = {} } = action;
    const reload = async () => {
      await refreshSiteTree(action.context?.site || selectedRoom?.site, {
        buildingId: idOf(action.context?.building, "BuildingId"),
        floorId: idOf(action.context?.floor, "FloorId"),
      });
      if (type === "room" && selectedRoom) {
        setSelectedRoom(null);
      }
    };

    if (type === "building") {
      await runAction(
        {
          LocationId: locationId,
          BuildingId: idOf(row, "BuildingId"),
          ActionType: "DeleteBuilding",
        },
        { reload, successFallback: "Building deleted." },
      );
      return;
    }
    if (type === "floor") {
      await runAction(
        {
          LocationId: locationId,
          BuildingId: idOf(context.building, "BuildingId"),
          FloorId: idOf(row, "FloorId"),
          ActionType: "DeleteFloor",
        },
        { reload, successFallback: "Floor deleted." },
      );
      return;
    }
    if (type === "room") {
      await runAction(
        {
          LocationId: locationId,
          BuildingId: idOf(context.building, "BuildingId"),
          FloorId: idOf(context.floor, "FloorId"),
          RoomId: idOf(row, "RoomId"),
          ActionType: "DeleteRoom",
        },
        { reload, successFallback: "Room deleted." },
      );
      return;
    }
    if (type === "capacity") {
      await runAction(
        {
          LocationId: locationId,
          BuildingId: idOf(context.building || selectedRoom?.building, "BuildingId"),
          FloorId: idOf(context.floor || selectedRoom?.floor, "FloorId"),
          RoomId:
            idOf(row, "RoomId") ||
            idOf(context.room || selectedRoom?.room, "RoomId"),
          RoomCapacityId: idOf(row, "RoomCapacityId", "RoomTypeId"),
          ActionType: "DeleteRoomCapacity",
        },
        { reload: () => refreshSiteTree(context.site || selectedRoom?.site), successFallback: "Capacity deleted." },
      );
    }
  };

  const openBlock = (type, row, context = {}) => {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    setBlockModal({ type, row, context });
    setBlockValues({
      reason: "",
      startDate: iso,
      startTime: "00:00",
      endDate: iso,
      endTime: "23:59",
    });
  };

  const handleUnblock = async (type, row, site = null) => {
    const authRes = await openAuthModal({ showDescription: false });
    if (!authRes || authRes.Success !== 1) return;
    const map = {
      building: {
        BuildingId: idOf(row, "BuildingId"),
        ActionType: "UnBlockBuilding",
      },
      floor: { FloorId: idOf(row, "FloorId"), ActionType: "UnBlockFloor" },
      room: { RoomId: idOf(row, "RoomId"), ActionType: "UnBlockRoom" },
      capacity: {
        RoomCapacityId: idOf(row, "RoomCapacityId", "RoomTypeId"),
        ActionType: "UnBlockRoomCapacity",
      },
    };
    const payload = map[type];
    if (!payload) return;
    await runAction(payload, {
      reload: () => refreshSiteTree(site || selectedRoom?.site),
      successFallback: "Unblocked successfully.",
    });
  };

  const handleConfirmBlock = async () => {
    if (!blockModal) return;
    if (!blockValues.reason.trim()) {
      showAlert({
        title: "Validation",
        message: "Please enter a block reason.",
        type: "warning",
      });
      return;
    }
    const authRes = await openAuthModal({ showDescription: false });
    if (!authRes || authRes.Success !== 1) return;
    const start = toLegacyBlockDateTime(
      blockValues.startDate,
      blockValues.startTime,
    );
    const end = toLegacyBlockDateTime(blockValues.endDate, blockValues.endTime);
    const { type, row } = blockModal;
    const map = {
      building: {
        BuildingId: idOf(row, "BuildingId"),
        ActionType: "BlockBuilding",
      },
      floor: { FloorId: idOf(row, "FloorId"), ActionType: "BlockFloor" },
      room: { RoomId: idOf(row, "RoomId"), ActionType: "BlockRoom" },
      capacity: {
        RoomCapacityId: idOf(row, "RoomCapacityId", "RoomTypeId"),
        ActionType: "BlockRoomCapacity",
      },
    };
    const base = map[type];
    if (!base) return;
    const ok = await runAction(
      {
        ...base,
        BlockedReason: blockValues.reason.trim(),
        BlockStartDateTime: start,
        BlockEndDateTime: end,
      },
      { reload: () => refreshSiteTree(blockModal.context?.site || selectedRoom?.site), successFallback: "Blocked successfully." },
    );
    if (ok) {
      setBlockModal(null);
      setBlockValues(EMPTY_BLOCK);
    }
  };

  const blockTitleFor = (row) => {
    if (!isBlocked(row)) return "";
    const reason = nameOf(row, "BlockedReason") || "Blocked";
    const from = nameOf(row, "BlockStartDateTime");
    const to = nameOf(row, "BlockEndDateTime");
    return `(click to unblock) Reason: ${reason}${from ? `, from ${from}` : ""}${to ? ` to ${to}` : ""}`;
  };

  const formTitle = useMemo(() => {
    if (!formModal) return "";
    const { kind, entity } = formModal;
    const editing = Boolean(entity && kind !== "capacity");
    return (
      {
        building: editing ? "Edit building" : "Add building",
        floor: editing ? "Edit floor" : "Add floor",
        room: editing ? "Edit room" : "Add room",
        capacity: "Add bed / chair",
      }[kind] || ""
    );
  }, [formModal]);

  const selectedPath = selectedRoom
    ? [
        siteLabel(selectedRoom.site),
        nameOf(selectedRoom.building, "BuildingName"),
        nameOf(selectedRoom.floor, "FloorName"),
        nameOf(selectedRoom.room, "RoomName"),
      ]
        .filter(Boolean)
        .join(" / ")
    : "";

  const selectedRoomId = selectedRoom
    ? idOf(selectedRoom.room, "RoomId")
    : "";
  const capacityFormOpen = formModal?.kind === "capacity";

  React.useEffect(() => {
    setFormModal((prev) => (prev?.kind === "capacity" ? null : prev));
  }, [selectedRoomId]);

  return (
    <div className="capacity-mgmt-page">
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Admin" },
          { label: "Capacity Management" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: "Refresh",
            onClick: async () => {
              setSiteCache({});
              siteCacheRef.current = {};
              setSelectedRoom(null);
              await loadSites();
            },
          },
        ]}
      />

      <div className="capacity-mgmt capacity-mgmt--tree-layout">
        <section className="capacity-mgmt__tree-panel">
          <header className="capacity-mgmt__panel-head">
            <div>
              <h3>Location tree</h3>
              <p>Site → Building → Floor → Room</p>
            </div>
            <Link to="/sitemanagement" className="capacity-mgmt__sites-link">
              Manage sites
            </Link>
          </header>

          <div className="capacity-mgmt__search">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Search sites…"
              value={treeSearch}
              onChange={(e) => setTreeSearch(e.target.value)}
            />
          </div>

          <div className="capacity-mgmt__tree-scroll">
            {!filteredSites.length ? (
              <p className="capacity-mgmt__blank">
                No active sites. Add sites in Site Management first.
              </p>
            ) : (
              filteredSites.map((site) => {
                const siteId = idOf(site, "SiteId");
                const siteOpen = Boolean(expandedSites[siteId]);
                const cache = siteCache[siteId];
                const locationId = cache?.locationId || "";
                const buildings = cache?.buildings || [];

                return (
                  <div key={siteId} className="capacity-mgmt__node is-site">
                    <div className="capacity-mgmt__node-row">
                      <button
                        type="button"
                        className="capacity-mgmt__expand"
                        onClick={() => toggleSite(site)}
                        aria-expanded={siteOpen}
                      >
                        <i
                          className={`fas fa-chevron-${siteOpen ? "down" : "right"}`}
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        className="capacity-mgmt__node-label"
                        onClick={() => toggleSite(site)}
                      >
                        <i className="fas fa-hospital" aria-hidden="true" />
                        <span>{siteLabel(site)}</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary capacity-mgmt__add-btn"
                        title="Add building"
                        disabled={!locationId && !siteOpen}
                        onClick={async () => {
                          const siteId = idOf(site, "SiteId");
                          setExpandedSites((current) => ({
                            ...current,
                            [siteId]: true,
                          }));
                          showLoader();
                          try {
                            const data = await loadSiteTree(site);
                            openForm("building", null, {
                              site,
                              locationId: data?.locationId,
                            });
                          } finally {
                            hideLoader();
                          }
                        }}
                      >
                        <i className="fas fa-plus" aria-hidden="true" />
                      </button>
                    </div>

                    {siteOpen ? (
                      <div className="capacity-mgmt__children">
                        {cache?.loading ? (
                          <p className="capacity-mgmt__hint">Loading…</p>
                        ) : !buildings.length ? (
                          <p className="capacity-mgmt__hint">
                            No buildings yet. Add a building to continue.
                          </p>
                        ) : (
                          buildings.map((building) => {
                            const buildingId = idOf(building, "BuildingId");
                            const buildingOpen = Boolean(
                              expandedBuildings[buildingId],
                            );
                            const floors =
                              cache?.byBuilding?.[buildingId]?.floors || [];
                            return (
                              <div
                                key={buildingId}
                                className={`capacity-mgmt__node is-building${isBlocked(building) ? " is-blocked" : ""}`}
                              >
                                <div className="capacity-mgmt__node-row">
                                  <button
                                    type="button"
                                    className="capacity-mgmt__expand"
                                    onClick={() => toggleBuilding(buildingId)}
                                  >
                                    <i
                                      className={`fas fa-chevron-${buildingOpen ? "down" : "right"}`}
                                      aria-hidden="true"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="capacity-mgmt__node-label"
                                    onClick={() => toggleBuilding(buildingId)}
                                  >
                                    <i
                                      className="fas fa-building"
                                      aria-hidden="true"
                                    />
                                    <span>
                                      {nameOf(building, "BuildingName") ||
                                        "Building"}
                                    </span>
                                  </button>
                                  <RowActions
                                    blocked={isBlocked(building)}
                                    blockTitle={blockTitleFor(building)}
                                    onEdit={() =>
                                      openForm("building", building, {
                                        site,
                                        locationId,
                                      })
                                    }
                                    onDelete={() =>
                                      requestDelete({
                                        type: "building",
                                        row: building,
                                        locationId,
                                        context: { site },
                                      })
                                    }
                                    onBlock={() =>
                                      openBlock("building", building, {
                                        site,
                                        locationId,
                                      })
                                    }
                                    onUnblock={() =>
                                      handleUnblock("building", building, site)
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary capacity-mgmt__add-btn"
                                    title="Add floor"
                                    onClick={() =>
                                      openForm("floor", null, {
                                        site,
                                        locationId,
                                        building,
                                      })
                                    }
                                  >
                                    <i className="fas fa-plus" aria-hidden="true" />
                                  </button>
                                </div>

                                {buildingOpen ? (
                                  <div className="capacity-mgmt__children">
                                    {!floors.length ? (
                                      <p className="capacity-mgmt__hint">
                                        No floors in this building.
                                      </p>
                                    ) : (
                                      floors.map((floor) => {
                                        const floorId = idOf(floor, "FloorId");
                                        const floorOpen = Boolean(
                                          expandedFloors[floorId],
                                        );
                                        const pack =
                                          cache?.byBuilding?.[buildingId]
                                            ?.roomsByFloor?.[floorId] || {
                                            rooms: [],
                                            capacities: [],
                                          };
                                        return (
                                          <div
                                            key={floorId}
                                            className={`capacity-mgmt__node is-floor${isBlocked(floor) ? " is-blocked" : ""}`}
                                          >
                                            <div className="capacity-mgmt__node-row">
                                              <button
                                                type="button"
                                                className="capacity-mgmt__expand"
                                                onClick={() =>
                                                  toggleFloor(floorId)
                                                }
                                              >
                                                <i
                                                  className={`fas fa-chevron-${floorOpen ? "down" : "right"}`}
                                                  aria-hidden="true"
                                                />
                                              </button>
                                              <button
                                                type="button"
                                                className="capacity-mgmt__node-label"
                                                onClick={() =>
                                                  toggleFloor(floorId)
                                                }
                                              >
                                                <i
                                                  className="fas fa-layer-group"
                                                  aria-hidden="true"
                                                />
                                                <span>
                                                  {nameOf(floor, "FloorName") ||
                                                    "Floor"}
                                                </span>
                                              </button>
                                              <RowActions
                                                blocked={isBlocked(floor)}
                                                blockTitle={blockTitleFor(floor)}
                                                onEdit={() =>
                                                  openForm("floor", floor, {
                                                    site,
                                                    locationId,
                                                    building,
                                                  })
                                                }
                                                onDelete={() =>
                                                  requestDelete({
                                                    type: "floor",
                                                    row: floor,
                                                    locationId,
                                                    context: {
                                                      site,
                                                      building,
                                                    },
                                                  })
                                                }
                                                onBlock={() =>
                                                  openBlock("floor", floor, {
                                                    site,
                                                    locationId,
                                                    building,
                                                  })
                                                }
                                                onUnblock={() =>
                                                  handleUnblock("floor", floor, site)
                                                }
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary capacity-mgmt__add-btn"
                                                title="Add room"
                                                onClick={() =>
                                                  openForm("room", null, {
                                                    site,
                                                    locationId,
                                                    building,
                                                    floor,
                                                  })
                                                }
                                              >
                                                <i className="fas fa-plus" aria-hidden="true" />
                                              </button>
                                            </div>

                                            {floorOpen ? (
                                              <div className="capacity-mgmt__children">
                                                {!pack.rooms.length ? (
                                                  <p className="capacity-mgmt__hint">
                                                    No rooms on this floor.
                                                  </p>
                                                ) : (
                                                  pack.rooms.map((room) => {
                                                    const roomId = idOf(
                                                      room,
                                                      "RoomId",
                                                    );
                                                    const selected =
                                                      selectedRoom &&
                                                      idOf(
                                                        selectedRoom.room,
                                                        "RoomId",
                                                      ) === roomId;
                                                    const caps = (
                                                      pack.capacities || []
                                                    ).filter(
                                                      (c) =>
                                                        capacityRoomRefId(
                                                          c,
                                                        ).toLowerCase() ===
                                                        roomId.toLowerCase(),
                                                    );
                                                    return (
                                                      <div
                                                        key={roomId}
                                                        className={`capacity-mgmt__node is-room${selected ? " is-selected" : ""}${isBlocked(room) ? " is-blocked" : ""}`}
                                                      >
                                                        <div className="capacity-mgmt__node-row">
                                                          <span className="capacity-mgmt__expand-spacer" />
                                                          <button
                                                            type="button"
                                                            className="capacity-mgmt__node-label"
                                                            onClick={() =>
                                                              selectRoom(
                                                                site,
                                                                locationId,
                                                                building,
                                                                floor,
                                                                room,
                                                              )
                                                            }
                                                          >
                                                            <i
                                                              className="fas fa-door-open"
                                                              aria-hidden="true"
                                                            />
                                                            <span>
                                                              {nameOf(
                                                                room,
                                                                "RoomName",
                                                              ) || "Room"}
                                                            </span>
                                                            <small>
                                                              {caps.length}{" "}
                                                              bed/chair
                                                            </small>
                                                          </button>
                                                          <RowActions
                                                            blocked={isBlocked(
                                                              room,
                                                            )}
                                                            blockTitle={blockTitleFor(
                                                              room,
                                                            )}
                                                            onEdit={() =>
                                                              openForm(
                                                                "room",
                                                                room,
                                                                {
                                                                  site,
                                                                  locationId,
                                                                  building,
                                                                  floor,
                                                                },
                                                              )
                                                            }
                                                            onDelete={() =>
                                                              requestDelete({
                                                                type: "room",
                                                                row: room,
                                                                locationId,
                                                                context: {
                                                                  site,
                                                                  building,
                                                                  floor,
                                                                },
                                                              })
                                                            }
                                                            onBlock={() =>
                                                              openBlock(
                                                                "room",
                                                                room,
                                                                {
                                                                  site,
                                                                  locationId,
                                                                  building,
                                                                  floor,
                                                                },
                                                              )
                                                            }
                                                            onUnblock={() =>
                                                              handleUnblock(
                                                                "room",
                                                                room,
                                                                site,
                                                              )
                                                            }
                                                          />
                                                        </div>
                                                      </div>
                                                    );
                                                  })
                                                )}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="capacity-mgmt__detail-panel">
          {!selectedRoom ? (
            <div className="capacity-mgmt__detail-empty">
              <i className="fas fa-bed" aria-hidden="true" />
              <h3>Room capacity</h3>
              <p>
                Select a <strong>room</strong> in the location tree to view its
                beds and chairs here.
              </p>
            </div>
          ) : (
            <>
              <header className="capacity-mgmt__panel-head">
                <div>
                  <h3>
                    {nameOf(selectedRoom.room, "RoomName") || "Room"} capacity
                  </h3>
                  <p>{selectedPath}</p>
                </div>
                {!capacityFormOpen ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() =>
                      openForm("capacity", selectedRoom.room, {
                        site: selectedRoom.site,
                        locationId: selectedRoom.locationId,
                        building: selectedRoom.building,
                        floor: selectedRoom.floor,
                        room: selectedRoom.room,
                      })
                    }
                  >
                    <i className="fas fa-plus me-1" aria-hidden="true" />
                    Add
                  </button>
                ) : null}
              </header>

              {capacityFormOpen ? (
                <div className="capacity-mgmt__inline-add">
                  <div className="capacity-mgmt__inline-add-field">
                    <label htmlFor="capacity-count">Count</label>
                    <input
                      id="capacity-count"
                      type="number"
                      min={1}
                      className="form-control form-control-sm"
                      value={formValues.count}
                      onChange={(e) =>
                        setFormValues((c) => ({
                          ...c,
                          count: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="capacity-mgmt__inline-add-field">
                    <span className="capacity-mgmt__inline-add-label">Type</span>
                    <div
                      className="capacity-mgmt__type-toggle"
                      role="group"
                      aria-label="Capacity type"
                    >
                      <button
                        type="button"
                        className={
                          formValues.type === "Bed" ? "is-active" : undefined
                        }
                        onClick={() =>
                          setFormValues((c) => ({ ...c, type: "Bed" }))
                        }
                      >
                        <i className="fas fa-bed" aria-hidden="true" />
                        Bed
                      </button>
                      <button
                        type="button"
                        className={
                          formValues.type === "Chair" ? "is-active" : undefined
                        }
                        onClick={() =>
                          setFormValues((c) => ({ ...c, type: "Chair" }))
                        }
                      >
                        <i className="fas fa-chair" aria-hidden="true" />
                        Chair
                      </button>
                    </div>
                  </div>
                  <div className="capacity-mgmt__inline-add-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveForm}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={closeForm}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="capacity-mgmt__capacity-stats">
                <span>
                  <strong>
                    {
                      selectedCaps.filter((c) => capacityTypeLabel(c) === "Bed")
                        .length
                    }
                  </strong>{" "}
                  beds
                </span>
                <span>
                  <strong>
                    {
                      selectedCaps.filter(
                        (c) => capacityTypeLabel(c) === "Chair",
                      ).length
                    }
                  </strong>{" "}
                  chairs
                </span>
              </div>

              {!selectedCaps.length ? (
                <div className="capacity-mgmt__detail-empty is-soft">
                  <p>No beds or chairs have been added to this room yet.</p>
                </div>
              ) : (
                <div className="capacity-mgmt__capacity-grid">
                  {selectedCaps.map((cap) => {
                    const type = capacityTypeLabel(cap);
                    const isChair = type === "Chair";
                    const capName =
                      nameOf(cap, "RoomCapacityName", "CapacityName") || type;
                    const capId = idOf(cap, "RoomCapacityId", "RoomTypeId");
                    const blocked = isBlocked(cap);
                    return (
                      <article
                        key={capId || capName}
                        className={`capacity-mgmt__capacity-card${isChair ? " is-chair" : " is-bed"}${blocked ? " is-blocked" : ""}`}
                      >
                        <div className="capacity-mgmt__capacity-card-main">
                          <i
                            className={isChair ? "fas fa-chair" : "fas fa-bed"}
                            aria-hidden="true"
                            title={type}
                          />
                          <strong title={type}>{capName}</strong>
                        </div>
                        <RowActions
                          blocked={blocked}
                          blockTitle={blockTitleFor(cap)}
                          onDelete={() =>
                            requestDelete({
                              type: "capacity",
                              row: {
                                ...cap,
                                RoomId: idOf(selectedRoom.room, "RoomId"),
                              },
                              locationId: selectedRoom.locationId,
                              context: {
                                building: selectedRoom.building,
                                floor: selectedRoom.floor,
                                room: selectedRoom.room,
                              },
                            })
                          }
                          onBlock={() =>
                            openBlock("capacity", cap, {
                              building: selectedRoom.building,
                              floor: selectedRoom.floor,
                              room: selectedRoom.room,
                            })
                          }
                          onUnblock={() =>
                            handleUnblock(
                              "capacity",
                              cap,
                              selectedRoom?.site,
                            )
                          }
                        />
                      </article>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {formModal && formModal.kind !== "capacity" ? (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{formTitle}</h5>
                <button type="button" className="btn-close" onClick={closeForm} />
              </div>
              <div className="modal-body">
                {formModal.kind === "room" && !formModal.entity ? (
                  <p className="mb-0 text-muted">
                    A new room will be created on the selected floor.
                  </p>
                ) : null}
                {formModal.kind !== "room" || formModal.entity ? (
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formValues.name}
                      onChange={(e) =>
                        setFormValues((c) => ({
                          ...c,
                          name: e.target.value,
                        }))
                      }
                      autoFocus
                    />
                  </div>
                ) : null}
                {formModal.kind === "building" && !formModal.entity ? (
                  <div className="mb-3">
                    <label className="form-label">Number of buildings</label>
                    <input
                      type="number"
                      min={1}
                      className="form-control"
                      value={formValues.count}
                      onChange={(e) =>
                        setFormValues((c) => ({
                          ...c,
                          count: e.target.value,
                        }))
                      }
                    />
                  </div>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeForm}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveForm}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {blockModal ? (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Block {blockModal.type}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setBlockModal(null)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Reason</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={blockValues.reason}
                    onChange={(e) =>
                      setBlockValues((c) => ({ ...c, reason: e.target.value }))
                    }
                  />
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label">Start date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={blockValues.startDate}
                      onChange={(e) =>
                        setBlockValues((c) => ({
                          ...c,
                          startDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Start time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={blockValues.startTime}
                      onChange={(e) =>
                        setBlockValues((c) => ({
                          ...c,
                          startTime: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label">End date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={blockValues.endDate}
                      onChange={(e) =>
                        setBlockValues((c) => ({
                          ...c,
                          endDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label">End time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={blockValues.endTime}
                      onChange={(e) =>
                        setBlockValues((c) => ({
                          ...c,
                          endTime: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setBlockModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmBlock}
                >
                  Block
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, action: null })}
        onConfirm={handleConfirmDelete}
        title="Confirm delete"
        message="Are you sure you want to delete this item?"
        confirmText="Delete"
        confirmButtonClass="btn-danger"
        icon="fas fa-trash text-danger"
      />
    </div>
  );
}
